import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import Hls from 'hls.js';
import { BunnyMapService } from '../../shared/services/bunny-map.service';
import { ControllerService } from '../../shared/services/controller.service';

@Component({
  selector: 'app-bunny-rating-video',
  templateUrl: './bunny-video.component.html',
  styleUrls: ['./bunny-video.component.scss'],
  standalone: false,
})
export class BunnyRatingVideoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: any[] = [];
  @Input() startAt: number = 0;
  @Input() skipToEnd: boolean = false;
  @Input() showVote: boolean = false;
  @Input() question: string = '';
  @Input() previewing: boolean = false;

  @ViewChild('videoEl', { static: true }) videoElRef!: ElementRef<HTMLVideoElement>;

  @Output() endSection = new EventEmitter<{}>();
  @Output() finishedMedia = new EventEmitter<{}>();
  @Output() playedMedia = new EventEmitter<{}>();
  @Output() startedMedia = new EventEmitter<{}>();
  @Output() emitRating = new EventEmitter<{}>();

  currentTime: number = 0;
  duration: number = 0;
  progressValue: number = 0;
  underPlay: boolean = false;
  showShowPlay: boolean = false;
  sentPlayed: boolean = false;
  lastUpdated: number = 0;
  updateInterval: number = 1000;
  currentObject: any;
  posterUrl: string = '';

  private hls: Hls | null = null;
  private listenerAbort: AbortController | null = null;

  private get video(): HTMLVideoElement {
    return this.videoElRef.nativeElement;
  }

  constructor(
    private route: ActivatedRoute,
    private bunnyMapService: BunnyMapService,
    private controller: ControllerService,
  ) {}

  ngOnInit() {}

  ngOnDestroy() {
    this.listenerAbort?.abort();
    this.hls?.destroy();
    this.controller.toggleLoading(false);
  }

  ngOnChanges(_changes: SimpleChanges) {
    this.loadNewGame();
  }

  @HostListener('window:resize')
  onResize() {
    setTimeout(() => this.videobgEnlarge(), 10);
  }

  onMediaTimeUpdate(data: { seconds: number; percent: number; duration: number }) {
    this.currentTime = data.seconds;
    this.duration = data.duration;
    this.progressValue = data.percent * 100;
    if (data.percent > 0) {
      this.controller.toggleLoading(false);
    }
  }

  onMediaEnd() {
    this.currentTime = this.duration;
    this.progressValue = 100;
    this.showVote = true;
    this.finishedMedia.emit({});
  }

  onPlay() {
    this.controller.toggleLoading(true);
    this.underPlay = true;
    this.showShowPlay = false;
    this.video.play().catch(() => {
      this.underPlay = false;
      this.showShowPlay = true;
      this.controller.toggleLoading(false);
    });
    this.videobgEnlarge();
    setTimeout(() => this.videobgEnlarge(), 10);

    if (this.skipToEnd) {
      if (this.video.duration) {
        this.video.currentTime = this.video.duration - 2;
      } else {
        this.video.addEventListener('loadedmetadata', () => {
          this.video.currentTime = this.video.duration - 2;
        }, { once: true });
      }
    }
  }

  onPause(paused: { underPlay: boolean; loadNew: boolean }) {
    this.underPlay = paused.underPlay;
    this.video.pause();
    if (paused.loadNew) {
      this.loadNewGame();
    }
  }

  onVote(makeVote: { voted: boolean; votedPreValue: number; votedValue: number }) {
    this.showVote = makeVote.voted;
  }

  onSubmitVote(vote: any) {
    this.finishedMedia.emit({});
    this.emitRating.emit({
      isVideo: true,
      value: vote.votedValue,
      ...this.currentObject,
    });
    this.video.pause();
    this.underPlay = false;
  }

  onClickProgress(e: MouseEvent) {
    const target = e.target as HTMLElement;
    const x = e.pageX - target.offsetLeft;
    const clickedValue = x / target.clientWidth;
    if (clickedValue < 1) {
      const newTime = Math.floor(clickedValue * this.duration);
      this.video.currentTime = newTime;
      this.onMediaTimeUpdate({ seconds: newTime, duration: this.duration, percent: newTime / this.duration });
    }
  }

  checkIfPlaying() {
    this.controller.toggleLoading(false);
    if (!this.underPlay) {
      this.showShowPlay = true;
    }
  }

  videobgEnlarge() {
    const videoBgAspect = document.querySelector<HTMLElement>('.videobg-aspect');
    const videoBgWidth = document.querySelector<HTMLElement>('.videobg-width');
    if (!videoBgAspect || !videoBgWidth) return;

    const videoAspect = videoBgAspect.offsetHeight / videoBgAspect.offsetWidth;
    const windowAspect = window.innerHeight / window.innerWidth;
    if (windowAspect > videoAspect) {
      videoBgWidth.style.width = (windowAspect / videoAspect) * 100 + '%';
    } else {
      videoBgWidth.style.width = '100%';
    }
  }

  private loadNewGame() {
    this.showVote = this.previewing;

    if (this.startAt < this.items.length) {
      this.currentObject = this.items[this.startAt];
      const vimeoId = this.currentObject.video;
      const bunnyId = this.currentObject.bunnyId || this.bunnyMapService.getBunnyId(vimeoId);
      const src = this.bunnyMapService.getPlaylistUrl(bunnyId);
      this.posterUrl = this.bunnyMapService.getThumbnailUrl(bunnyId);

      this.controller.toggleLoading(true);
      this.sentPlayed = false;
      this.showShowPlay = false;
      this.currentTime = 0;
      this.progressValue = 0;
      this.duration = 0;

      this.listenerAbort?.abort();
      this.listenerAbort = new AbortController();
      const { signal } = this.listenerAbort;

      this.hls?.destroy();

      if (Hls.isSupported()) {
        this.hls = new Hls();
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => this.onPlay());
        this.hls.loadSource(src);
        this.hls.attachMedia(this.video);
      } else {
        this.video.src = src;
        this.video.addEventListener('loadedmetadata', () => this.onPlay(), { once: true, signal });
      }

      this.video.addEventListener('loadedmetadata', () => {
        this.duration = this.video.duration;
      }, { once: true, signal });

      this.video.volume = 1;

      this.video.addEventListener('timeupdate', () => {
        const current = this.video.currentTime;
        const duration = this.video.duration;
        const percent = duration ? current / duration : 0;

        if (!this.sentPlayed) {
          this.sentPlayed = true;
          this.startedMedia.emit({ sourceId: this.currentObject.id });
        }

        if (!this.lastUpdated || this.lastUpdated + this.updateInterval < Date.now()) {
          this.lastUpdated = Date.now();
          this.onMediaTimeUpdate({ seconds: current, percent, duration });
        }
      }, { signal });

      this.video.addEventListener('ended', () => {
        this.currentTime = this.duration;
        this.onMediaEnd();
      }, { signal });

      setTimeout(() => this.checkIfPlaying(), 3000);
      this.videobgEnlarge();

    } else if (this.startAt === this.items.length && this.currentObject !== undefined) {
      this.listenerAbort?.abort();
      this.hls?.destroy();
      this.endSection.emit({});
    }
  }
}
